if (!customElements.get('product-form')) { 
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        
        // ICR Upsell properties
        this.isICRUpsellProduct = this.checkICRUpsellProduct();
        this.icrButtonShown = false;
      }

      /**
       * Get the currently active cart based on A/B testing state
       * This ensures we always use the correct cart component
       */
      getCurrentActiveCart() {
        // If A/B testing is active, use its active cart
        if (window.cartABTesting && typeof window.cartABTesting.getActiveCart === 'function') {
          const activeCart = window.cartABTesting.getActiveCart();
          if (activeCart) {
            return activeCart;
          }
        }
        
        // If CartABTestingAPI proxy is available, use it to determine active cart
        if (window.CartABTestingAPI && !window.CartABTestingAPI._isProxy) {
          try {
            const activeCart = window.CartABTestingAPI.getActiveCart();
            if (activeCart) {
              return activeCart;
            }
          } catch (error) {
            // Silently fall back to detection
          }
        }
        
        // Check for visible, non-inactive carts
        const cartDrawer = document.querySelector('cart-drawer:not([data-cart-ab-inactive])');
        const cartNotification = document.querySelector('cart-notification:not([data-cart-ab-inactive])');
        
        // Prefer cart drawer if it's available and not hidden
        if (cartDrawer && cartDrawer.style.display !== 'none') {
          return cartDrawer;
        }
        
        // Fall back to cart notification if available and not hidden
        if (cartNotification && cartNotification.style.display !== 'none') {
          return cartNotification;
        }
        
        // Check cached cart reference if no A/B testing conflicts
        if (this.cart && !this.cart.hasAttribute('data-cart-ab-inactive')) {
          return this.cart;
        }
        
        // Final fallback
        return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        
        // Get the currently active cart (essential for A/B testing)
        const currentCart = this.getCurrentActiveCart();
        if (currentCart) {
          const sectionsToRender = currentCart.getSectionsToRender();
          const sectionIds = sectionsToRender.map((section) => section.id);

          formData.append('sections', sectionIds.join(','));
          formData.append('sections_url', window.location.pathname);
          // Force fresh cart data by adding cache-busting timestamp
          formData.append('_t', Date.now());
          currentCart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else {
              // Get the currently active cart for rendering (essential for A/B testing)
              const currentCart = this.getCurrentActiveCart();
              if (!currentCart) {
                window.location = window.routes.cart_url;
                return;
              }

              if (!this.error)
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id') });
              this.error = false;
              
                            // Validate sections before rendering
              const expectedSections = currentCart.getSectionsToRender?.().map(s => s.id) || [];
              const responseSections = Object.keys(response.sections || {});
              const hasCorrectSections = expectedSections.every(section => responseSections.includes(section));
              
              if (!hasCorrectSections) {
                // If we're in drawer mode but got main-cart-items, force a refresh with correct sections
                if (currentCart.tagName.toLowerCase() === 'cart-drawer' && responseSections.includes('main-cart-items')) {
                  if (window.cartABTesting && typeof window.cartABTesting.refreshCartContents === 'function') {
                    window.cartABTesting.refreshCartContents(currentCart);
                  }
                  return;
                }
              }
              
              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    currentCart.renderContents(response);
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              } else {
                currentCart.renderContents(response);
                // Check for ICR upsell after successful cart addition
                if (this.isICRUpsellProduct && !this.icrButtonShown) {
                  this.showICRUpsellButton().catch(error => {
                    console.warn('Error showing ICR upsell button:', error);
                  });
                }
              }
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      // ICR Upsell functionality
      checkICRUpsellProduct() {
        try {
                  // Check if ICR upsell is enabled in theme settings
        if (!window.themeSettings?.icr_upsell_enabled) {
          return false;
        }

        // Check if ICR adapter product is available/in stock
        if (!window.themeSettings?.icr_adapter_available) {
          return false;
        }

          // Get product data from data-product-json script
          const productJson = document.querySelector('[data-product-json]');
          if (!productJson) {
            return false;
          }

          const productData = JSON.parse(productJson.textContent);
          return productData.tags && productData.tags.includes('ICRUpsell');
        } catch (error) {
          return false;
        }
      }

      async checkICRAdapterInCart() {
        try {
          // Get current product ID
          const currentProductId = this.dataset.productId || 
                                  window.productId || 
                                  this.closest('[data-product-id]')?.dataset.productId ||
                                  document.querySelector('[data-product-id]')?.dataset.productId ||
                                  window.ShopifyAnalytics?.meta?.product?.id ||
                                  window.meta?.product?.id;
          
          if (!currentProductId) {
            console.warn('Could not determine current product ID for ICR check');
            return false;
          }

          // Get ICR adapter product ID
          const icrAdapterProductId = window.themeSettings?.icr_adapter_product_id;
          if (!icrAdapterProductId) {
            console.warn('ICR adapter product ID not configured');
            return false;
          }

          // Check cart for ICR adapters
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();

          // Look for ICR adapters that are either:
          // 1. Added specifically for this product (has _icr_for_product property)
          // 2. Any general ICR adapter in cart (product ID matches)
          const hasICRAdapter = cart.items.some(item => {
            const isICRAdapterProduct = item.product_id.toString() === icrAdapterProductId.toString();
            if (!isICRAdapterProduct) return false;

            // Check if it's specifically for this product
            if (item.properties?._icr_for_product === currentProductId.toString()) {
              return true;
            }

            // Or if it's a general ICR adapter (no specific product targeting)
            if (!item.properties?._icr_for_product && !item.properties?._icr_for_line_item) {
              return true;
            }

            return false;
          });

          return hasICRAdapter;
        } catch (error) {
          console.warn('Error checking ICR adapter in cart:', error);
          return false; // Default to showing upsell if we can't check
        }
      }

      async showICRUpsellButton() {
        if (this.icrButtonShown || !this.submitButton) return;
        
        // Check if ICR adapter is already in cart for this product
        const isICRAdapterInCart = await this.checkICRAdapterInCart();
        if (isICRAdapterInCart) {
          console.log('ICR adapter already in cart, skipping upsell display');
          return;
        }
        
        // Create ICR upsell container
        const icrContainer = document.createElement('div');
        icrContainer.className = 'icr-upsell-container icr-upsell-container--hidden';
        icrContainer.innerHTML = `
          <div class="icr-upsell-content">
            <div class="icr-upsell-text">
              ${window.themeSettings?.icr_upsell_text || "Does your bike need an Internal Cable Routing Adapter?"}
            </div>
            <div class="icr-upsell-buttons">
              <button type="button" class="button button--secondary icr-learn-more-btn">
                <span>Learn more</span>
              </button>
              <button type="button" class="button button--primary icr-add-cart-btn">
                <span>Add to cart</span> 
              </button>
            </div>
          </div>
        `;
        
        // Insert ICR container below the original button (keep original button visible)
        this.submitButton.parentNode.insertBefore(icrContainer, this.submitButton.nextSibling);
        
        // Trigger animation after a brief delay to ensure DOM is ready
        requestAnimationFrame(() => {
          icrContainer.classList.remove('icr-upsell-container--hidden');
          icrContainer.classList.add('icr-upsell-container--animating');
        });
        
        // Add event listeners
        const learnMoreBtn = icrContainer.querySelector('.icr-learn-more-btn');
        const addCartBtn = icrContainer.querySelector('.icr-add-cart-btn');
        
        learnMoreBtn.addEventListener('click', this.onICRLearnMoreHandler.bind(this));
        addCartBtn.addEventListener('click', this.onICRAddCartHandler.bind(this));
        
        // Store references
        this.icrContainer = icrContainer;
        this.icrLearnMoreBtn = learnMoreBtn;
        this.icrAddCartBtn = addCartBtn;
        
        this.icrButtonShown = true;
      }

      onICRLearnMoreHandler() {
        // Get ICR adapter product handle for navigation
        const icrProductHandle = window.themeSettings?.icr_adapter_product_handle;
        
        if (!icrProductHandle) {
          console.error('ICR adapter product not configured');
          return;
        }

        // Navigate directly to the ICR product page
        window.location.href = `/products/${icrProductHandle}`;
      }

      onICRAddCartHandler() {
        if (this.icrAddCartBtn.getAttribute('aria-disabled') === 'true') return;

        this.setICRButtonLoading(this.icrAddCartBtn, true);
        this.addICRAdapterToCart();
      }

      async addICRAdapterToCart() {
        try {
      
          // Get ICR adapter product handle from theme settings
          const icrProductHandle = window.themeSettings?.icr_adapter_product_handle;
          
          if (!icrProductHandle) {
            this.handleICRError('ICR adapter product not configured');
            return;
          }

          // First, fetch the ICR product to get the variant ID
          const productResponse = await fetch(`/products/${icrProductHandle}.js`);
          if (!productResponse.ok) {
            throw new Error('Failed to fetch ICR adapter product');
          }
          
          const icrProduct = await productResponse.json();
          const variantId = icrProduct.variants?.[0]?.id;
          
          if (!variantId) {
            throw new Error('ICR adapter product has no available variants');
          }

          // CONSOLIDATION FIX: Check if ICR adapter already exists for this product

          // Try multiple methods to get product ID
          const targetProductId = this.dataset.productId || 
                                 window.productId || 
                                 this.closest('[data-product-id]')?.dataset.productId ||
                                 document.querySelector('[data-product-id]')?.dataset.productId ||
                                 window.ShopifyAnalytics?.meta?.product?.id ||
                                 window.meta?.product?.id;
          
          const existingICRItem = await this.findExistingICRAdapter(targetProductId, variantId);
          
          if (existingICRItem) {
            console.log('🔄 ICR adapter already exists, updating quantity instead of adding new item');
            await this.updateICRAdapterQuantity(existingICRItem.key, existingICRItem.quantity + 1);
            
            // Hide ICR upsells in cart drawer after successful consolidation
            this.triggerCartDrawerICRUpsellHide();
            // Also hide the PDP ICR upsell since we already have the adapter
            this.hideICRUpsellAfterSuccess();
            return;
          }

          // Add ICR adapter to cart
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          config.headers['X-ICR-Request'] = 'true'; // Exempt from performance manager batching
          delete config.headers['Content-Type'];

          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', 1);
          
          // Add properties to match cart drawer ICR additions for consolidation
          // When added from PDP, use current product as the target
          const currentProductId = this.dataset.productId || 
                                  window.productId || 
                                  this.closest('[data-product-id]')?.dataset.productId ||
                                  document.querySelector('[data-product-id]')?.dataset.productId ||
                                  window.ShopifyAnalytics?.meta?.product?.id ||
                                  window.meta?.product?.id;
          
          if (currentProductId) {
            formData.append('properties[_icr_for_product]', currentProductId);
            formData.append('properties[_icr_source]', 'pdp'); // Track source
          }
          
          const icrCart = this.getCurrentActiveCart();
          if (icrCart) {
            formData.append(
              'sections',
              icrCart.getSectionsToRender().map((section) => section.id).join(',')
            );
            formData.append('sections_url', window.location.pathname);
            icrCart.setActiveElement(document.activeElement);

          }
          config.body = formData;

          const response = await fetch(`${routes.cart_add_url}`, config);
          const responseData = await response.json();

          if (responseData.status) {
            throw new Error(responseData.description || 'Failed to add ICR adapter to cart');
          }

          // Success - render cart contents and show success message
          const renderCart = this.getCurrentActiveCart();
          if (renderCart) {
            renderCart.renderContents(responseData);
          }
          
          this.showICRSuccessMessage();
          
          // Hide ICR upsells in cart drawer after successful addition
          this.triggerCartDrawerICRUpsellHide();
          
        } catch (error) {
          this.handleICRError(error.message);
        } finally {
          this.setICRButtonLoading(this.icrAddCartBtn, false);
        }
      }

      setICRButtonLoading(button, loading) {
        if (loading) {
          button.setAttribute('aria-disabled', true);
          button.classList.add('loading');
          // Create spinner if it doesn't exist
          if (!button.querySelector('.loading-overlay__spinner')) {
            const spinner = document.createElement('div');
            spinner.className = 'loading-overlay__spinner';
            spinner.innerHTML = `
              <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
              </svg>
            `;
            button.appendChild(spinner);
          }
          button.querySelector('.loading-overlay__spinner').classList.remove('hidden');
        } else {
          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
          const spinner = button.querySelector('.loading-overlay__spinner');
          if (spinner) {
            spinner.classList.add('hidden');
          }
        }
      }

      showICRSuccessMessage() {  
        if (this.icrAddCartBtn) {
          // Updated 2024 - Show simple "Added" text
          const buttonSpan = this.icrAddCartBtn.querySelector('span');
          if (buttonSpan) {
            buttonSpan.textContent = 'Added';
          } else {
            this.icrAddCartBtn.textContent = 'Added';
          }
          
          // After 3 seconds, revert back to "Add to cart"
          setTimeout(() => {
            if (this.icrAddCartBtn) {
              const currentButtonSpan = this.icrAddCartBtn.querySelector('span');
              if (currentButtonSpan) {
                currentButtonSpan.textContent = 'Add to cart';
              } else {
                this.icrAddCartBtn.textContent = 'Add to cart';
              }
            }
          }, 3000);
        }
        
        // Hide ICR container after successful addition
        this.hideICRUpsellAfterSuccess();
      }

      hideICRUpsellAfterSuccess() {
        if (!this.icrContainer) return;
        
        // Apply smooth hiding animation
        this.icrContainer.style.transition = 'opacity 0.3s ease, max-height 0.3s ease';
        this.icrContainer.style.opacity = '0';
        this.icrContainer.style.maxHeight = '0px';
        this.icrContainer.style.overflow = 'hidden';
        
        // After animation completes, completely hide the container
        setTimeout(() => {
          if (this.icrContainer) {
            this.icrContainer.style.display = 'none';
            // Reset button shown flag to prevent re-showing
            this.icrButtonShown = false;
          }
        }, 300);
      }

      // CONSOLIDATION FIX: Find existing ICR adapter in cart for the same product
      async findExistingICRAdapter(productId, variantId) {
        try {
  
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();

          // Look for existing ICR adapter with matching product ID or variant ID
          const icrAdapterItems = cart.items.filter(item => item.variant_id === variantId);

          const existingItem = cart.items.find(item => {
            // Check if it's the same variant (ICR adapter product)
            if (item.variant_id === variantId) {

              // Check if it's for the same product (via properties)
              if (item.properties && item.properties._icr_for_product === productId) {

                return true;
              }
              // Also check if it has no specific product context (generic ICR adapter)
              if (!item.properties || (!item.properties._icr_for_product && !item.properties._icr_for_line_item)) {

                return true;
              }
            }
            return false;
          });

          return existingItem || null;
        } catch (error) {
          console.warn('PDP: Failed to check for existing ICR adapter:', error);
          return null;
        }
      }

      // CONSOLIDATION FIX: Update quantity of existing ICR adapter
      async updateICRAdapterQuantity(itemKey, newQuantity) {
        try {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          const formData = new FormData();
          formData.append('id', itemKey);
          formData.append('quantity', newQuantity);
          
          const icrCart = this.getCurrentActiveCart();
          if (icrCart) {
            formData.append(
              'sections',
              icrCart.getSectionsToRender().map((section) => section.id).join(',')
            );
            formData.append('sections_url', window.location.pathname);
            icrCart.setActiveElement(document.activeElement);
          }
          config.body = formData;

          const response = await fetch(`${routes.cart_change_url}`, config);
          const responseData = await response.json();

          if (responseData.status) {
            throw new Error(responseData.description || 'Failed to update ICR adapter quantity');
          }

          // Success - render cart contents and show success message
          const renderCart = this.getCurrentActiveCart();
          if (renderCart) {
            renderCart.renderContents(responseData);
          }
          
          this.showICRSuccessMessage();
          
        } catch (error) {
          console.error('Failed to update ICR adapter quantity:', error);
          this.handleICRError(error.message || 'Failed to update ICR adapter quantity');
        }
      }

      // Trigger cart drawer to hide ICR upsells after PDP ICR addition
      triggerCartDrawerICRUpsellHide() {
        try {
          // Method 1: Find cart drawer instance and call its hide method
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && cartDrawer.hideICRUpsellsForCompletedItems) {
            cartDrawer.hideICRUpsellsForCompletedItems();
            return;
          }
          
          // Method 2: Dispatch custom event for cart drawer to listen to
          document.dispatchEvent(new CustomEvent('icrAdapterAdded', {
            detail: { source: 'pdp' }
          }));
          
        } catch (error) {
          console.warn('PDP: Failed to trigger cart drawer ICR upsell hide:', error);
        }
      }

      handleICRError(errorMessage) {
        // Show error in the existing error message wrapper
        this.handleErrorMessage(`ICR Upsell Error: ${errorMessage}`);
        
        // Reset ICR container to original state
        if (this.icrContainer) {
          this.icrContainer.remove();
          this.icrButtonShown = false;
        }
      }
    }
  );
}
